import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Each id doubles as the data-theme value matched by a block in
// style/themes.css - adding a theme means adding an entry here and a matching
// selector there, nothing else.
//
// animatedBackground is the starfield switch: themes that set it false get a
// plain gradient instead (see [data-bg-animation="off"] in parallax-star.css).
export const THEMES = [
    { id: 'cyclone', animatedBackground: true },
    { id: 'plain-blue', animatedBackground: false },
    { id: 'dracula', animatedBackground: true },
    { id: 'nord', animatedBackground: true },
    { id: 'tokyo-night', animatedBackground: true },
    { id: 'catppuccin', animatedBackground: true },
    { id: 'gruvbox', animatedBackground: true },
    { id: 'one-dark', animatedBackground: true },
    { id: 'lotr', animatedBackground: false },
    { id: 'win98', animatedBackground: false },
    { id: 'macos', animatedBackground: false },
    { id: 'valorant', animatedBackground: true },
    { id: 'solarized-light', animatedBackground: false },
    { id: 'sakura', animatedBackground: false }
];

export const DEFAULT_THEME = 'cyclone';
const STORAGE_KEY = 'theme';

function findTheme(id) {
    return THEMES.find((theme) => theme.id === id);
}

function detectDefaultTheme() {
    const stored = localStorage.getItem(STORAGE_KEY);
    return findTheme(stored) ? stored : DEFAULT_THEME;
}

// Written to <html> rather than a wrapper element this provider renders: the
// tokens have to be in scope for the fixed-position overlays (voice panel,
// toasts, knock modal) too, and those mount as siblings of the app shell
// rather than inside it.
function applyTheme(id) {
    const theme = findTheme(id) || findTheme(DEFAULT_THEME);
    const root = document.documentElement;
    root.setAttribute('data-theme', theme.id);
    root.setAttribute('data-bg-animation', theme.animatedBackground ? 'on' : 'off');
}

// At import time, not in an effect - by the time React has mounted and run
// effects the first paint is already on screen, which would show up as a
// flash of the default palette on every launch.
applyTheme(detectDefaultTheme());

// The Electron shell paints three surfaces before this renderer exists at all
// - the window background, the loading window and (Windows) the title bar
// overlay - and it can't read the theme out of localStorage from the main
// process. So hand it the resolved colors after every change; it caches them
// and paints the NEXT launch with them. See electron-starter.js.
//
// Deliberately the resolved values rather than a theme id: the shell then
// needs no copy of the palette, and a theme added to themes.css alone is
// picked up with no main-process change at all.
const SHELL_TOKENS = {
    background: '--bg-page',
    accent: '--accent-glow',
    accentSolid: '--accent',
    accentHover: '--accent-hover',
    text: '--text',
    textMuted: '--text-muted',
    danger: '--danger-text',
    fill: '--fill'
};

// Read through a probe element's computed `color` rather than straight off
// getPropertyValue: tokens are declared in whatever notation suited them
// (#hex, rgb(), and rgba(var(--rgb-text), .08) all appear in themes.css),
// and this collapses every one of them to a plain resolved rgb()/rgba() -
// which is what the main process validates before painting with it.
function reportShellColors() {
    if (!window.cyclone || !window.cyclone.setShellColors || !document.body) return;
    const probe = document.createElement('span');
    probe.style.display = 'none';
    document.body.appendChild(probe);
    try {
        const colors = {};
        for (const [key, token] of Object.entries(SHELL_TOKENS)) {
            probe.style.color = `var(${token})`;
            colors[key] = getComputedStyle(probe).color;
        }
        window.cyclone.setShellColors(colors);
    } finally {
        probe.remove();
    }
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(detectDefaultTheme);

    useEffect(() => {
        applyTheme(theme);
        // After applyTheme, so the probe reads the new theme's values - and in
        // an effect rather than alongside the import-time applyTheme() call
        // above, which runs before <body> exists for the probe to mount into.
        reportShellColors();
    }, [theme]);

    const setTheme = useCallback((id) => {
        if (!findTheme(id)) return;
        localStorage.setItem(STORAGE_KEY, id);
        setThemeState(id);
    }, []);

    const value = useMemo(() => ({
        theme,
        setTheme,
        themes: THEMES
    }), [theme, setTheme]);

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    return useContext(ThemeContext);
}
